'use client';

import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useUpdateJobStatus } from '@/hooks/usePreInspection';
import { PreInspectionApiError } from '@/lib/pre-inspection/api';
import type { AppointmentTransitionCode } from '@/lib/pre-inspection/appointment';

interface AppointmentActionModalProps {
  jobId: string;
  toStatusCode: AppointmentTransitionCode;
  onClose: () => void;
}

const NOTES_MAX = 500;
const PHASE2_BANNER =
  'Full scheduling form (date, time, location) is coming in a future update. This confirms the status change only.';

export function AppointmentActionModal({
  jobId,
  toStatusCode,
  onClose,
}: AppointmentActionModalProps) {
  const titleId = useId();
  const bannerId = useId();
  const [notes, setNotes] = useState<string>('');

  const mutation = useUpdateJobStatus(jobId, {
    onSuccess: () => onClose(),
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (mutation.isPending) return;
      onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mutation.isPending, onClose]);

  function handleConfirm() {
    const trimmed = notes.trim();
    const payload: { status: AppointmentTransitionCode; notes?: string } = {
      status: toStatusCode,
    };
    if (trimmed.length > 0) payload.notes = trimmed;
    mutation.mutate(payload);
  }

  const panel = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bannerId}
        className="relative z-10 w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
      >
        <h2 id={titleId} className="text-lg font-semibold text-gray-900">
          Schedule Appointment
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Moves the job to &ldquo;Appointment Scheduled&rdquo;.
        </p>

        <div
          id={bannerId}
          role="note"
          className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          {PHASE2_BANNER}
        </div>

        <div className="mt-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Notes (optional)</span>
            <textarea
              aria-label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={NOTES_MAX}
              rows={3}
              disabled={mutation.isPending}
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            />
          </label>
          <p
            data-testid="appointment-modal-notes-counter"
            aria-live="polite"
            className="mt-1 text-xs text-gray-500"
          >
            {notes.length}/{NOTES_MAX}
          </p>
        </div>

        {mutation.isError && (
          <div
            role="alert"
            className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {mutation.error instanceof PreInspectionApiError
              ? mutation.error.message
              : 'Failed to update status.'}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={mutation.isPending}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {mutation.isPending ? 'Scheduling…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(panel, document.body);
}
